import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, X, Clock, Eye, Trash2, Loader2, Search, 
  Filter, ExternalLink, Mail, User, Calendar 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubmissions = () => {
  const { getAuthHeaders } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions`, getAuthHeaders());
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (status) => {
    if (!selectedSubmission) return;
    setReviewing(true);
    
    try {
      await axios.put(
        `${API}/admin/submissions/${selectedSubmission.id}/review`,
        { status, admin_notes: reviewNotes },
        getAuthHeaders()
      );
      
      // Update local state
      setSubmissions(prev => prev.map(s => 
        s.id === selectedSubmission.id 
          ? { ...s, status, admin_notes: reviewNotes }
          : s
      ));
      
      setSelectedSubmission(null);
      setReviewNotes('');
    } catch (error) {
      console.error('Failed to review submission:', error);
    } finally {
      setReviewing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    
    try {
      await axios.delete(`${API}/admin/submissions/${deleteId}`, getAuthHeaders());
      setSubmissions(prev => prev.filter(s => s.id !== deleteId));
    } catch (error) {
      console.error('Failed to delete submission:', error);
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 border-0"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700 border-0"><Check className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 border-0"><X className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return null;
    }
  };

  const filteredSubmissions = submissions.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.submitter_email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || s.status === activeTab;
    return matchesSearch && matchesTab;
  });

  const pendingCount = submissions.filter(s => s.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tool Submissions</h1>
          <p className="text-gray-500">Review and manage user-submitted tools</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">
            {pendingCount} pending review
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-white border border-gray-100 p-1">
          <TabsTrigger value="pending" className="relative">
            Pending
            {pendingCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                {pendingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by tool name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Submissions List */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {filteredSubmissions.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredSubmissions.map((submission) => (
              <div key={submission.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start gap-4">
                  <img
                    src={submission.logo}
                    alt={submission.name}
                    className="w-14 h-14 rounded-xl object-cover bg-gray-100"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/100?text=?';
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{submission.name}</h3>
                      {getStatusBadge(submission.status)}
                    </div>
                    <p className="text-sm text-gray-500 mb-2 line-clamp-1">
                      {submission.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {submission.submitter_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {submission.submitter_email}
                      </span>
                      <span>{submission.category}</span>
                      <span>{submission.pricing}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedSubmission(submission);
                        setReviewNotes(submission.admin_notes || '');
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {submission.status === 'pending' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          onClick={() => {
                            setSelectedSubmission(submission);
                            handleReview('approved');
                          }}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setReviewNotes('');
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteId(submission.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No submissions found</p>
          </div>
        )}
      </div>

      {/* Review Dialog */}
      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedSubmission && (
            <>
              <DialogHeader>
                <DialogTitle>Review Submission</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Tool Info */}
                <div className="flex items-start gap-4">
                  <img
                    src={selectedSubmission.logo}
                    alt={selectedSubmission.name}
                    className="w-16 h-16 rounded-xl object-cover"
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedSubmission.name}
                    </h3>
                    <p className="text-sm text-gray-500">{selectedSubmission.category}</p>
                    {getStatusBadge(selectedSubmission.status)}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">{selectedSubmission.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Pricing:</span>
                    <span className="ml-2 font-medium">{selectedSubmission.pricing}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Type:</span>
                    <span className="ml-2 font-medium capitalize">{selectedSubmission.priceType}</span>
                  </div>
                  {selectedSubmission.website_url && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Website:</span>
                      <a 
                        href={selectedSubmission.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-2 text-emerald-600 hover:text-emerald-700"
                      >
                        {selectedSubmission.website_url}
                        <ExternalLink className="w-3 h-3 inline ml-1" />
                      </a>
                    </div>
                  )}
                </div>

                {selectedSubmission.features?.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-500">Features:</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedSubmission.features.map((feature, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 rounded text-sm">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Submitter Info</h4>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4 text-gray-400" />
                      {selectedSubmission.submitter_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {selectedSubmission.submitter_email}
                    </span>
                  </div>
                </div>

                {selectedSubmission.status === 'pending' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Admin Notes (optional)
                    </label>
                    <Textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add notes about this submission..."
                      rows={3}
                    />
                  </div>
                )}

                {selectedSubmission.admin_notes && selectedSubmission.status !== 'pending' && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-sm font-medium text-gray-700">Admin Notes:</span>
                    <p className="text-sm text-gray-600 mt-1">{selectedSubmission.admin_notes}</p>
                  </div>
                )}
              </div>

              {selectedSubmission.status === 'pending' && (
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedSubmission(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleReview('rejected')}
                    disabled={reviewing}
                  >
                    {reviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
                    Reject
                  </Button>
                  <Button
                    className="bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => handleReview('approved')}
                    disabled={reviewing}
                  >
                    {reviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    Approve
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Submission</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this submission? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminSubmissions;
